import React from "react";
import { connect } from "react-redux";
import { Guid } from "guid-typescript";

import Dialog from "../dialog/Dialog";
import DialogClose from "../dialog/DialogClose";
import DialogContent from "../dialog/DialogContent";
import DialogFooter from "../dialog/DialogFooter";
import DialogOK from "../dialog/DialogOK";
import Panel from "../panels/Panel";
import { IAppState } from "../../state/Store";
import DialogCancel from "../dialog/DialogCancel";
import SelectorBox from "../selector/SelectorBox";
import MessagingContact from "../../types/core/MessagingContact";
import Checkbox from "../inputform/Checkbox";
import RichText from "../inputform/RichText";
import DatePicker from "../inputform/DatePicker";
import RadioButton from "../inputform/RadioButton";
import Spacer from "../containers/Spacer";
import { ReportSharePackage, Share } from "../../types/system/Share";
import { initialiseClientSystems } from "../../state/core/ClientSystem";
import { initialiseMessaging, sendMessage } from "../../state/system/Messaging";
import ClientSystem from "../../types/core/ClientSystem";
import Rendering from "../../foundation/Rendering";
import Datastore from "../../communication/Datastore";
import Utility from "../../foundation/Utility";
import { addAlert } from "../../state/system/Alerts";
import AlertEntry from "../../types/system/AlertEntry";
import ApplicationState from "../../foundation/AppState";
import Message from "../../types/system/Message";
import { setPrintMenuOpen } from "../../state/system/UIConfiguration";

import "./ShareMenu.scss";

interface ShareMenuProps {
    open: boolean;
    contacts: Array<MessagingContact>;

    messagingStatus: string;
    clientSystemsStatus: string;
    clientSystems: ClientSystem[];

    customPrintName: string;

    shareClosed: Function;

    addAlert: (alert: AlertEntry) => void;
    initialiseMessaging: () => {};
    initialiseClientSystems: () => {};
    sendMessage: (message: Message) => Promise<void>;
    setPrintMenuOpen: (open: boolean) => void;
}

interface ShareMenuState {
    isOpen: boolean;
    isLoading: boolean;
    canPrint: boolean;
    shareMode: string;
    shareType: string;
    shareLink: string;
    message: string;
    error?: string;
    clearTimeout?: ReturnType<typeof setTimeout>;
    recipients: Array<MessagingContact>;
    managementPortal?: ClientSystem;
    expiry: Date | null;
    minExpiry: Date | null;
    maxExpiry: Date | null;
    shareDetails?: any;
}

class ShareMenu extends React.Component<ShareMenuProps, ShareMenuState> {
    state = {
        isOpen: false,
        isLoading: false,
        canPrint: false,
        shareMode: "0",
        shareType: "0",
        shareLink: "",
        message: "",
        recipients: [],
        expiry: new Date().addDays(1).dateOnly(),
        minExpiry: new Date().addDays(1).dateOnly(),
        maxExpiry: new Date().addDays(30).dateOnly(),
    } as ShareMenuState;

    static getDerivedStateFromProps(
        props: ShareMenuProps,
        state: ShareMenuState,
    ): ShareMenuState {
        if (
            props.clientSystemsStatus === "succeeded" &&
            !state.managementPortal
        ) {
            const managementPortal = props.clientSystems.find(
                item => item.name === "Management Portal",
            );
            if (managementPortal) {
                return {
                    ...state,
                    managementPortal,
                };
            }
        }

        return state;
    }

    componentDidMount(): void {
        this.props.initialiseMessaging();
        this.props.initialiseClientSystems();
    }

    componentDidUpdate(
        prevProps: Readonly<ShareMenuProps>,
        prevState: Readonly<ShareMenuState>,
        snapshot?: any,
    ): void {
        if (prevProps.open === false && this.props.open === true) {
            this.setState({
                shareMode: "0",
                shareType: "0",
                shareLink: "",
                message: "",
                recipients: [],
                expiry: new Date().addDays(1).dateOnly(),
            });
        }
    }

    isLoading() {
        return (
            this.state.isLoading ||
            this.props.messagingStatus === "loading" ||
            this.props.clientSystemsStatus === "loading"
        );
    }

    setIsOpen(value: boolean) {
        this.setState(() => ({
            isOpen: value,
        }));
    }

    setIsLoading(value: boolean) {
        this.setState(() => ({
            isLoading: value,
        }));
    }

    contactDisplay(item: MessagingContact) {
        return `${item.Display} (${item.Username})`;
    }

    addRecipient(contact: MessagingContact) {
        this.setState(prevState => ({
            recipients: [...prevState.recipients, contact],
        }));
    }

    removeRecipient(contact: MessagingContact) {
        this.setState(prevState => ({
            recipients: prevState.recipients.filter(
                c => c.UserID !== contact.UserID,
            ),
        }));
    }

    async copyLink() {
        if (!!this.state.clearTimeout) clearTimeout(this.state.clearTimeout);

        await navigator.clipboard.writeText(this.state.shareLink ?? "");
        this.setState({ error: "Link copied" });

        this.setState({
            clearTimeout: setTimeout(() => {
                if (this.state.error === "Link copied") {
                    this.setState({ error: "" });
                }
                this.setState({ clearTimeout: undefined });
            }, 1000),
        });
    }

    async createShare(): Promise<Share | undefined> {
        if (!this.state.shareDetails) {
            if (!this.state.managementPortal) {
                this.setState({
                    error: "There is a configuration error with your account.",
                });
            } else if (
                this.state.shareMode === "1" &&
                (this.state.recipients?.length ?? 0) === 0
            ) {
                this.setState({
                    error: "Please select at least one person to share with.",
                });
            } else if (
                this.state.shareType === "1" &&
                (this.state.message?.length ?? 0) === 0
            ) {
                this.setState({
                    error: "Please provide a message to send in your email.",
                });
            } else {
                const pageName = Rendering.getReportTitle();
                const name =
                    Rendering.getReportTitle().replace(/:/g, " -") +
                    "_" +
                    new Date().toTimestamp() +
                    ".pdf";
                const title = this.props.customPrintName ?? pageName;

                // await this.autoPagerService.setPrintFormat();
                const body = Rendering.renderPageReportContent();
                // await this.autoPagerService.unSetPrintFormat();

                const expireAfter = Math.ceil(
                    this.state.expiry?.daysBetween(new Date().dateOnly()) ?? 0,
                );
                const userIds =
                    this.state.recipients?.map(item => item.UserID) ?? [];

                this.setState({
                    shareDetails: await Datastore.postType<Share>(
                        "api/Sharing",
                        {
                            details: {
                                id: Guid.create().toString(),
                                name: name,
                                title: title,
                                mime: "text/html",
                                expireAfter: expireAfter,
                                canPrint: this.state.canPrint ?? false,
                                userIDs: userIds,
                            },
                            data: await Utility.compressString(body),
                        } as ReportSharePackage,
                    ),
                });

                await Utility.sleep();
            }
        }

        return this.state.shareDetails;
    }

    async createLink() {
        this.setState({ isLoading: true });

        const details = await this.createShare();

        this.setState({
            shareLink: details
                ? this.state.managementPortal?.applicationPath +
                  "/Interface/Report/" +
                  details.id
                : "",
            isLoading: false,
        });
    }

    async sendEmail() {
        try {
            this.setState({ isLoading: true });

            const details = await this.createShare();

            if (!!details) {
                const link =
                    this.state.managementPortal?.applicationPath +
                    "/Interface/Report/" +
                    details.id;
                const user = ApplicationState.jwt;
                const title = Rendering.getReportTitle();

                await Promise.all([
                    this.props.sendMessage(
                        Message.createGeneric(
                            title,
                            `
                                    <p>
                                        ${
                                            user?.given_name ?? "[Unknown User]"
                                        } shared the report ${
                                details.name
                            } with you
                                        on Timetabling Solutions.
                                    </p>
                                </td>
                            </tr>
                        </table><br />
                        <table class="main tble" valign="top" style="table-layout:fixed">
                            <tr>
                                <td align="center" class="content"
                                    style="padding:20px;font-size:12pt;font-family:sans-serif;color:#333">
                                    <p>
                                        ${this.state.message}
                                    </p>
                                </td>
                            </tr>
                        </table><br />
                        <table class="main tble" valign="top" style="table-layout:fixed">
                            <tr>
                                <td align="center" class="content"
                                    style="padding:20px;font-size:12pt;font-family:sans-serif;color:#333">
                                    <p>
                                        Click the button below or copy and paste the URL into your browser
                                        in order to view the report.
                                    </p>
                                    <div>
                                        <table width="100%" class="display">
                                            <tr class="display">
                                                <td class="display nopad" align="center">
                                                    <div>
                                                        <!--[if mso]>
                                                        <v:roundrect href="${link}" style="width:400px;height:45px;v-text-anchor:middle;" arcsize="10%" stroke="f" fillcolor="#3394D0" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word">
                                                            <w:anchorlock/>
                                                            <v:textbox inset="0,0,0,0">
                                                                <center style="color:#ffffff;font-size:14pt">
                                                                    View Shared Report
                                                                </center>
                                                            </v:textbox>
                                                        </v:roundrect>
                                                        <![endif]-->
                                                        <!--[if !mso]>-->
                                                        <a class="btn" href="${link}">View Shared Report</a>
                                                        <!--<![endif]--></div>
                                                </td>
                                            </tr>
                                        </table>
                                    </div>
                                </td>
                            </tr>
                        </table><br />
                        <table class="main tble" valign="top" style="table-layout:fixed">
                            <tr>
                                <td align="center" class="content"
                                    style="padding:20px;font-size:12pt;font-family:sans-serif;color:#333">
                                    <p>
                                        If the above button does not open your browser you can
                                        copy and paste the below link into your browser and
                                        press enter.
                                    </p>
                                    <p class="link">${link}</p>`,
                            this.state.recipients?.map(item => item.UserID),
                        ),
                    ),
                    this.props.sendMessage(
                        Message.createMessage(
                            title,
                            `
                            <p>
                                ${
                                    user?.given_name ?? "[Unknown User]"
                                } shared the report ${details.name} with you
                                on Timetabling Solutions.
                            </p>
                            <p>
                                ${this.state.message}
                            </p>
                            <p>
                                <a class="btn" href="${link}">View Shared Report</a>
                            </p>
                            `,
                            this.state.recipients?.map(item => item.UserID),
                        ),
                    ),
                ]);

                this.props.shareClosed();

                this.props.addAlert({
                    text: "Share email successfully sent",
                });
            }
        } finally {
            this.setState({ isLoading: false });
        }
    }

    print() {
        this.props.setPrintMenuOpen(true);
    }

    render(): React.ReactNode {
        return (
            <Dialog
                dialogTitle="Share This Content"
                dialogIcon="ios_share"
                showDialog={this.props.open}
                showLoading={this.isLoading()}
                canResize={false}
                width={600}
                zIndex={99}>
                <DialogContent className="share-dialog flex">
                    <div className="share-ui col flex-column">
                        <div className="info">
                            This will share the information you're currently
                            viewing as it appears right now.
                        </div>

                        <Panel className="buffer top-clearance">
                            <h3>Who will have access to this?</h3>
                            <br />

                            <div className="flex-column">
                                <RadioButton
                                    name="ShareMode"
                                    icon="location_city"
                                    label="Anyone from this school"
                                    value="0"
                                    className="col"
                                    checked={this.state.shareMode === "0"}
                                    onChange={() =>
                                        this.setState({ shareMode: "0" })
                                    }
                                />

                                {this.state.shareMode === "0" && (
                                    <div className="info col left-clearance">
                                        Any user who is logged into this school
                                        can view the shared content.
                                    </div>
                                )}

                                <RadioButton
                                    name="ShareMode"
                                    icon="person_add"
                                    label="Share this with"
                                    value="1"
                                    className="col top-clearance-small"
                                    checked={this.state.shareMode === "1"}
                                    onChange={() =>
                                        this.setState({ shareMode: "1" })
                                    }
                                />

                                {this.state.shareMode === "1" && (
                                    <div className="info col left-clearance">
                                        Only the users specified can view the
                                        shared content.
                                    </div>
                                )}

                                {this.state.shareMode === "1" && (
                                    <SelectorBox
                                        className="col left-clearance top-clearance-small"
                                        inline={true}
                                        showName={false}
                                        keyField="UserID"
                                        displayField="Display"
                                        displayFunc={item =>
                                            this.contactDisplay(item)
                                        }
                                        placeholder="Enter name..."
                                        source={this.props.contacts}
                                        value={this.state.recipients}
                                        onAdd={this.addRecipient.bind(this)}
                                        onRemove={this.removeRecipient.bind(
                                            this,
                                        )}
                                    />
                                )}
                            </div>
                        </Panel>

                        <Panel className="buffer top-clearance">
                            <h3>Will people be able to print this?</h3>
                            <br />

                            <Checkbox
                                value={this.state.canPrint}
                                label="People with access can print"
                                onChange={value =>
                                    this.setState({ canPrint: value })
                                }
                            />
                        </Panel>

                        <Panel className="buffer top-clearance">
                            <h3>How should they receive it?</h3>
                            <br />

                            <div className="flex-column">
                                <RadioButton
                                    name="ShareType"
                                    icon="link"
                                    label="Give me a link I can share with them"
                                    value="0"
                                    className="col"
                                    checked={this.state.shareType === "0"}
                                    onChange={() =>
                                        this.setState({ shareType: "0" })
                                    }
                                />

                                {this.state.shareMode !== "0" && (
                                    <RadioButton
                                        name="ShareType"
                                        icon="mail"
                                        label="Send them an email"
                                        value="1"
                                        className="col"
                                        checked={this.state.shareType === "1"}
                                        onChange={() =>
                                            this.setState({
                                                shareType: "1",
                                            })
                                        }
                                    />
                                )}

                                {this.state.shareType !== "0" && (
                                    <RichText
                                        className="col top-clearance-small"
                                        minEdit={true}
                                        value={this.state.message}
                                        onChange={value =>
                                            this.setState({ message: value })
                                        }
                                        placeholder="Message..."
                                    />
                                )}
                            </div>
                        </Panel>

                        <Panel className="buffer top-clearance">
                            <h3>When should the share expire?</h3>
                            <br />

                            <DatePicker
                                className="full"
                                value={this.state.expiry}
                                minDate={this.state.minExpiry}
                                maxDate={this.state.maxExpiry}
                                onChange={date =>
                                    this.setState({ expiry: date })
                                }
                            />
                        </Panel>

                        {this.state.shareLink?.length > 0 && (
                            <Panel className="buffer top-clearance">
                                <h3>Your share link</h3>
                                <br />

                                <div className="flex">
                                    <input
                                        type="text"
                                        className="col"
                                        value={this.state.shareLink}
                                        readOnly></input>
                                    <button
                                        className="inline left-clearance"
                                        onClick={this.copyLink.bind(this)}
                                        tooltip-title="Copy link">
                                        <i>content_copy</i>
                                    </button>
                                </div>
                            </Panel>
                        )}
                    </div>
                </DialogContent>
                <DialogFooter full={true}>
                    <div className="flex-column full">
                        <div className="error bottom-clearance-small">
                            {this.state.error}
                        </div>
                        <div className="flex-row spaced full">
                            <DialogCancel
                                icon="print"
                                text="Print"
                                onClick={this.print.bind(this)}></DialogCancel>
                            <Spacer />
                            <DialogClose
                                text="Cancel"
                                onClick={this.props.shareClosed}></DialogClose>
                            {this.state.shareType === "0" ? (
                                <DialogOK
                                    onClick={this.createLink.bind(this)}
                                    icon="add_link"
                                    text="Create"></DialogOK>
                            ) : (
                                <DialogOK
                                    onClick={this.sendEmail.bind(this)}
                                    icon="send"
                                    text="Send"></DialogOK>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </Dialog>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { messaging, clientSystem, ui } = state.db;
    return {
        contacts: messaging.contacts,
        messagingStatus: messaging.initStatus,
        clientSystems: clientSystem.clientSystems,
        clientSystemsStatus: clientSystem.systemsStatus,
        customPrintName: ui.customPrintName,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        addAlert: (alert: AlertEntry) => dispatch(addAlert(alert)),
        initialiseMessaging: () => dispatch(initialiseMessaging()),
        initialiseClientSystems: () => initialiseClientSystems(),
        sendMessage: (message: Message) => dispatch(sendMessage(message)),
        setPrintMenuOpen: (open: boolean) => dispatch(setPrintMenuOpen(open)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ShareMenu);
